import clsx from 'clsx';
import React, { Children, cloneElement, forwardRef, ReactElement, ReactNode } from 'react';
import { Hint } from '../hint';
import { InputLabel } from '../input-label';
import { RadioProps } from '../radio';
import { Stack } from '../stack';
import { Typography } from '../typography';
import classes from './radio-group.module.scss';

export type RadioGroupProps = {
  /**
   * The label for the radio card group.
   */
  label?: ReactNode;

  /**
   * The description for the radio card group.
   */
  description?: ReactNode;

  /**
   * The children of the radio card group.
   */
  children: ReactElement<RadioProps>[];

  /**
   * The currently selected value
   */
  value: string;

  /**
   * Callback fired when the value changes
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;

  /**
   * If true, the radio card group will render an error state.
   */
  error?: boolean;

  /**
   * A hint to be displayed below the radio card group.
   */
  helper?: ReactNode;

  /**
   * The direction of the radio cards.
   * @default 'row'
   */
  direction?: 'row' | 'column';

  /**
   * A hint to be displayed below the radio card group.
   */
  hint?: ReactNode;

  /**
   * If true, the radio card group will render a required asterisk.
   */
  required?: boolean;
};

/**
 * A group of radio cards that allows selecting one option from multiple choices.
 * Manages the selected state and provides context for child RadioCard components.
 *
 * @example
 * ```tsx
 * <RadioGroup value={selected} onChange={setSelected}>
 *   <Radio value="option1" label="Option 1" />
 *   <Radio value="option2" label="Option 2" />
 * </RadioCardGroup>
 * ```
 */
export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      value,
      onChange,
      label,
      description,
      children,
      helper,
      error,
      direction = 'row',
      hint,
      required
    },
    ref
  ) => {
    return (
      <Stack direction={'column'} spacing={0.5} className={clsx(classes.root, { [classes.hasError]: error })} ref={ref}>
        {label && (
          <InputLabel
            hint={hint ? <Hint>{hint}</Hint> : undefined}
            required={required}
            as={'span'}
          >
            {label}
          </InputLabel>
        )}

        {description && (
          <Typography variant="caption" color={'grey.ultra-dark'}>
            {description}
          </Typography>
        )}

        <Stack direction={direction} spacing={3} crossAxis="stretch">
          {Children.map(children, child => cloneElement(child, {
            ...child.props,
            variant: 'compact',
            color: error ? 'danger' : undefined,
            checked: value === child.props.value,
            onChange,
          }))}
        </Stack>

        {helper && (
          <Typography variant="caption" className={classes.helper}>
            {helper}
          </Typography>
        )}
      </Stack>
    );
  }
);
